import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Tabs, Tab, Button, CircularProgress, Paper, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { projectsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ProjectBoard from '../components/pm/ProjectBoard';
import ProjectListView from '../components/pm/ProjectListView';
import SprintPanel from '../components/pm/SprintPanel';
import ColumnManager from '../components/pm/ColumnManager';
import ProjectDocs from '../components/pm/ProjectDocs';
import TaskCreateDialog from '../components/TaskCreateDialog';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

/**
 * Unified project workspace (internal + external). Tabs: Board | Liste | Sprints.
 * The backend 404s out-of-scope projects, so clients only ever reach their own.
 */
function ProjectWorkspacePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isClient } = useAuth();
  const clientUser = isClient && isClient();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const boardRef = useRef(null);

  const loadProject = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await projectsAPI.getById(projectId);
      setProject(res.data);
    } catch (err) {
      if (err.response && err.response.status === 404) setNotFound(true);
      console.error('[PM] project load error', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const handleTaskCreated = () => {
    setCreateOpen(false);
    if (boardRef.current) boardRef.current.reload();
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (notFound || !project) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Projekt nicht gefunden
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pm')}>
            Zur Projektübersicht
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/pm')} sx={{ mb: 1 }}>
            Projekte
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: project.color || '#1e3a5f' }} />
            <Typography variant="h4" fontWeight={700}>{project.name}</Typography>
            {project.customer_name && <Chip size="small" label={project.customer_name} />}
          </Box>
          {project.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {project.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!clientUser && tab === 0 && (
            <Button variant="outlined" startIcon={<ViewColumnIcon />} onClick={() => setColumnsOpen(true)}>
              Spalten verwalten
            </Button>
          )}
          {tab !== 3 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                color: 'white', fontWeight: 600,
                '&:hover': { background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)' },
              }}
            >
              Task erstellen
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Board" />
        <Tab label="Liste" />
        <Tab label="Sprints" />
        <Tab label="Dokumente" />
      </Tabs>

      {tab === 0 && <ProjectBoard ref={boardRef} projectId={project.id} />}
      {tab === 1 && <ProjectListView projectId={project.id} />}
      {tab === 2 && <SprintPanel project={project} />}
      {tab === 3 && <ProjectDocs projectId={project.id} />}

      <TaskCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={project.id}
        onTaskCreated={handleTaskCreated}
      />

      <ColumnManager
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        projectId={project.id}
        onChanged={() => { if (boardRef.current) boardRef.current.reload(); }}
      />
    </Container>
  );
}

export default ProjectWorkspacePage;
